import { Pressable, StyleSheet, View } from 'react-native';
import { RabblLink } from '@/components/RabblLink';
import { Image } from 'expo-image';
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from '@/utilities/AppConstants';
import * as AppConsts from '@/utilities/AppConsts';
import { Community } from "@/types/community";
import { AppText } from '@/components/AppText';
import { Link, RelativePathString } from 'expo-router';
import { CommunityMembership } from '@/types/community_membership';
import { CustomText, CustomTextStyles } from './CustomText';

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    padding: 20,
    alignItems: 'center',
    backgroundColor: 'transparent',
    gap: 20,
  },
  container: {
    width: '95%',
  },
  flex1: {
    flex: 1,
  },
  createButton: {
    paddingTop: 20,
    alignItems: 'center',
  },
  padding3: {
    padding: 3,
  },
  padding5: {
    padding: 5,
  },
  videoWrapper: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    paddingTop: 20,
    paddingBottom: 20,
    gap: 20,
  },
  mainWrapper: {
    alignItems: 'center',
  },
  membershipImageWrapper: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: AppConsts.PIP_WIDTH,
    height: AppConsts.PIP_HEIGHT,
  },
  membershipImage: {
    width: AppConsts.PIP_WIDTH,
    height: AppConsts.PIP_HEIGHT,
    borderWidth: 1,
  },
  membershipTextWrapper: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: AppConsts.PIP_WIDTH,
    alignItems: 'center',
    backgroundColor: AppConsts.COLOR2,
    borderWidth: 1,
  },
  communityNameOverTopWrapper: {
    position: 'absolute',
    top: 0,
    alignSelf: 'center',
    maxWidth: AppConsts.RABBL_SEARCH_RESULT_WIDTH,
    backgroundColor: AppConsts.COLOR2,
    borderWidth: 1,
    paddingHorizontal: 3,
    flexDirection: 'row',
    gap: 2,
    alignItems: 'center',
  },
  w16h16: {
    width: 16,
    height: 16,
  },
});

interface Props {
  community: Community,
  admin?: boolean,
  showDescription?: boolean,
  showAge?: boolean,
  community_membership?: CommunityMembership,
  communityNameOverTop?: boolean,
  communityNameUnderneath?: boolean,
  imageWidth?: number,
  imageHeight?: number,
  onPress?: (community: Community) => void,
  customLink?: string,
  communityMembershipLink?: boolean,
}

export function RabblSearchResult({
    community,
    admin = false,
    showDescription = false,
    showAge = true,
    community_membership = undefined,
    communityNameOverTop = false,
    communityNameUnderneath = true,
    imageWidth = AppConsts.RABBL_SEARCH_RESULT_WIDTH,
    imageHeight = AppConsts.RABBL_SEARCH_RESULT_HEIGHT,
    onPress = undefined,
    customLink = undefined,
    communityMembershipLink = false,
  } : Props) {
  const content = (
    <View style={styles.mainWrapper}>
      <View>
        <Image
          source={AppUtils.getCommunityImage(community)}
          alt={community.name}
          contentFit='contain'
          style={{
            width: imageWidth,
            height: imageHeight,
          }}
        />
        {
          community_membership &&
            <>
              <View style={styles.membershipImageWrapper}>
                <Image
                  source={
                    community_membership.profile.visuals.length > 0 ?
                        { uri: AppUtils.createBackendLink('/rabbl/api/visual?thumbnail=yes&uniqueid=' + community_membership.profile.visuals[0].uniqueid) }
                      :
                        require('@/assets/images/PixelarticonsUser80x80.png')
                  }
                  style={styles.membershipImage}
                  contentFit='contain'
                />
              </View>
              <View style={styles.membershipTextWrapper}>
                <AppText style={CustomTextStyles.linkTinyNoUnderline} numberOfLines={1} ellipsizeMode='tail'>
                  {
                    showAge ?
                        community_membership.profile.name + " / " + AppUtils.getMemberAgeByUserInfo(community_membership.user_info) + AppUtils.prefixIfNonEmpty(AppUtils.getMemberShortRegion(community_membership.user_info), " / ")
                      :
                        community_membership.profile.name + AppUtils.prefixIfNonEmpty(AppUtils.getMemberShortRegion(community_membership.user_info), " / ")
                  }
                  
                </AppText>
              </View>
            </>
        }
        {
          communityNameOverTop &&
            <View style={styles.communityNameOverTopWrapper}>
              <Image source={require('@/assets/images/butterflyicon16x16.png')} style={styles.w16h16} />
              <AppText style={[CustomTextStyles.linkTinyNoUnderline, { fontSize: 10 }]} numberOfLines={1} ellipsizeMode='tail'>
                {community.name}
              </AppText>
            </View>
        }
      </View>
      { communityNameUnderneath && 
          <>
            <View style={styles.padding5} />
            <CustomText style={CustomTextStyles.medium}>
              {community.name}
            </CustomText>
          </>
      }

      { showDescription && (
        <View>
          <View style={styles.padding3} />
          <AppText>
            {community.details}
          </AppText>
        </View>
      )}

      {admin && (
        <View>
          <View style={styles.padding3} />
          <AppText>
            {community.approved ? "Approved & Live" : "Waiting Approval"}
          </AppText>
        </View>
      )}
    </View>
  );

  if (!admin) {
    if (onPress) {
      return (
        <View key={community.uniqueid}>
          <Pressable onPress={() => onPress(community)}>
            {content}
          </Pressable>
        </View>
      );
    } else {
      if (customLink) {
        return (
          <View key={community.uniqueid}>
            <RabblLink community={community} href={customLink}>
              {content}
            </RabblLink>
          </View>
        );
      } else if (communityMembershipLink) {
        return (
          <View key={community.uniqueid}>
            <RabblLink community={community} href={"/member/" + community_membership?.uniqueid}>
              {content}
            </RabblLink>
          </View>
        );
      } else {
        return (
          <View key={community.uniqueid}>
            <RabblLink community={community}>
              {content}
            </RabblLink>
          </View>
        );
      }
    }
  } else {
    return (
      <View key={community.uniqueid}>
        <Link href={('/manage/' + community.uniqueid) as RelativePathString}>
          {content}
        </Link>
      </View>
    );
  }
}
